import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CAD_SERVICES } from "@/data/cadServices";
import { DISCIPLINES } from "@/data/disciplines";
import { cadIcon } from "@/components/navbar/navIcons";
import useBlogPosts from "@/hooks/useBlogPosts";
import PostCard from "@/components/blog/PostCard";
import { disciplineIcon } from "./disciplineIcons";

export function Faq({ d }) {
  return (
    <section className="bg-white border-b border-slate-200" data-testid="discipline-faq">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">FAQ</div>
          <h2 className="display mt-3 text-3xl sm:text-4xl font-extrabold tracking-tighter text-slate-900">Questions buyers ask before they start.</h2>
        </div>
        <Accordion type="single" collapsible className="lg:col-span-8 border-t border-slate-200">
          {d.faqs.map((f, i) => (
            <AccordionItem key={f.q} value={`faq-${i}`} className="border-slate-200" data-testid={`discipline-faq-item-${i}`}>
              <AccordionTrigger className="display text-left text-base sm:text-lg font-bold text-slate-900 hover:no-underline hover:text-topo">{f.q}</AccordionTrigger>
              <AccordionContent className="text-[15px] text-slate-600 leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

export function RelatedLinks({ d }) {
  const services = d.relatedCad.map((s) => CAD_SERVICES.find((x) => x.slug === s)).filter(Boolean);
  const others = DISCIPLINES.filter((x) => x.slug !== d.slug);
  return (
    <section className="bg-paper-dark/30 border-b border-slate-200" data-testid="discipline-related">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-14 grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div>
          <div className="mono text-[10px] tracking-[0.22em] uppercase text-slate-500">Related CAD Techies services</div>
          <ul className="mt-4 divide-y divide-slate-200 border-y border-slate-200">
            {services.map((s) => {
              const Icon = cadIcon(s);
              return (
                <li key={s.slug}>
                  <Link to={`/cad-techies#cad-${s.slug}`} data-testid={`discipline-related-cad-${s.slug}`} className="group flex items-center gap-3 py-3 text-slate-800 hover:text-topo">
                    <Icon className="h-4 w-4 text-slate-400 group-hover:text-topo" strokeWidth={1.75} />
                    <span className="font-semibold">{s.name}</span>
                    <ArrowRight className="ml-auto h-4 w-4 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
        <div>
          <div className="mono text-[10px] tracking-[0.22em] uppercase text-slate-500">Other disciplines</div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            {others.map((o) => {
              const Icon = disciplineIcon(o);
              return (
                <Link key={o.slug} to={`/disciplines/${o.slug}`} data-testid={`discipline-other-${o.slug}`} className="group flex items-center gap-3 border border-slate-200 bg-white p-4 hover:border-topo transition-colors">
                  <Icon className="h-5 w-5 text-topo" strokeWidth={1.75} />
                  <span className="text-sm font-semibold text-slate-900 group-hover:text-topo">{o.name}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

export function RelatedGuides({ category }) {
  const { posts } = useBlogPosts(category);
  if (!posts.length) return null;
  return (
    <section className="bg-white border-b border-slate-200 py-14" data-testid="discipline-guides">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="flex items-end justify-between gap-4">
          <div>
            <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">Buyer guides</div>
            <h2 className="display mt-2 text-2xl sm:text-3xl font-extrabold tracking-tighter text-slate-900">Read before you buy.</h2>
          </div>
          <Link to="/blog" className="text-sm font-semibold text-topo hover:text-orange-survey inline-flex items-center gap-1">All guides <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">{posts.slice(0, 3).map((p) => <PostCard key={p.slug} post={p} />)}</div>
      </div>
    </section>
  );
}
